import React, { useState, useEffect } from 'react';
import { Card, Statistic, Row, Col, Table, Button, message, Spin, Modal, Tag, Typography, Badge, List, Space, Divider } from 'antd';
import { PlusOutlined, PaperClipOutlined, ExclamationCircleOutlined, DownloadOutlined, EyeOutlined, SettingOutlined } from '@ant-design/icons';
import FilePreview from '../shared/FilePreview';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  getFinanceSummary,
  getFinanceRecords,
  deleteFinanceRecord,
  updateContractAmount,
  EntityType,
  type FinanceSummary,
  type FinanceRecord,
} from '../../../../services/finance';
import {
  getFinanceConfig,
} from '../../../../services/financeConfig';
import type { CaseFinanceFullConfig } from '../../../../types/financeConfig';
import { PaymentMode } from '../../../../types/financeConfig';
import AddCaseFinanceRecordModal from './AddCaseFinanceRecordModal';
import FinanceConfigModal from './FinanceConfigModal';
import MilestoneProgress from './MilestoneProgress';

const { Text } = Typography;

const { confirm } = Modal;

interface CaseFinanceTabProps {
  caseId: number;
  caseData: any;
  canEdit: boolean;
}

/**
 * 案件财务记录Tab组件
 * 显示财务汇总和财务记录列表
 * 参考LeadDrawer的FinanceTab实现
 */
const CaseFinanceTab: React.FC<CaseFinanceTabProps> = ({ caseId, caseData, canEdit }) => {
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [records, setRecords] = useState<FinanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<any[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);
  
  // 财务配置相关状态
  const [financeConfig, setFinanceConfig] = useState<CaseFinanceFullConfig | null>(null);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  
  // 关联线索财务数据
  const [leadSummary, setLeadSummary] = useState<FinanceSummary | null>(null);
  const [leadRecords, setLeadRecords] = useState<FinanceRecord[]>([]);
  const [leadLoading, setLeadLoading] = useState(false);

  /**
   * 加载财务配置
   */
  const loadFinanceConfig = async () => {
    try {
      setLoadingConfig(true);
      const data = await getFinanceConfig(caseId);
      setFinanceConfig(data);
    } catch (error: any) {
      // 如果没有配置，不显示错误
      console.log('No finance config or error loading:', error);
      setFinanceConfig(null);
    } finally {
      setLoadingConfig(false);
    }
  };

  /**
   * 加载财务汇总数据
   */
  const loadFinanceSummary = async () => {
    try {
      const data = await getFinanceSummary(EntityType.CASE, caseId);
      // Convert string amounts to numbers if needed
      const normalizedSummary: FinanceSummary = {
        contract_amount: typeof data.contract_amount === 'string' 
          ? parseFloat(data.contract_amount) 
          : data.contract_amount,
        total_received: typeof data.total_received === 'string' 
          ? parseFloat(data.total_received) 
          : data.total_received,
        total_refund: typeof data.total_refund === 'string' 
          ? parseFloat(data.total_refund) 
          : data.total_refund,
        total_receivable: typeof data.total_receivable === 'string' 
          ? parseFloat(data.total_receivable) 
          : data.total_receivable,
        net_received: typeof data.net_received === 'string' 
          ? parseFloat(data.net_received) 
          : data.net_received,
      };
      setSummary(normalizedSummary);
    } catch (error: any) {
      console.error('Failed to load finance summary:', error);
      // 不显示错误消息，因为可能还没有财务数据
    }
  };

  /**
   * 加载财务记录列表
   */
  const loadFinanceRecords = async () => {
    try {
      const data = await getFinanceRecords(EntityType.CASE, caseId);
      // Convert string amounts to numbers if needed and ensure required fields
      const normalizedRecords: FinanceRecord[] = (data.items || []).map(record => ({
        ...record,
        amount: typeof record.amount === 'string' 
          ? parseFloat(record.amount) 
          : record.amount,
        updated_at: record.updated_at || record.created_at,
      }));
      setRecords(normalizedRecords);
    } catch (error: any) {
      console.error('Failed to load finance records:', error);
      message.error('加载财务记录失败');
    }
  };

  /**
   * 加载关联线索财务汇总数据
   */
  const loadLeadFinanceSummary = async () => {
    if (!caseData?.lead_id) return;
    
    try {
      const data = await getFinanceSummary(EntityType.LEAD, caseData.lead_id);
      const normalizedSummary: FinanceSummary = {
        contract_amount: typeof data.contract_amount === 'string' 
          ? parseFloat(data.contract_amount) 
          : data.contract_amount,
        total_received: typeof data.total_received === 'string' 
          ? parseFloat(data.total_received) 
          : data.total_received,
        total_refund: typeof data.total_refund === 'string' 
          ? parseFloat(data.total_refund) 
          : data.total_refund,
        total_receivable: typeof data.total_receivable === 'string' 
          ? parseFloat(data.total_receivable) 
          : data.total_receivable,
        net_received: typeof data.net_received === 'string' 
          ? parseFloat(data.net_received) 
          : data.net_received,
      };
      setLeadSummary(normalizedSummary);
    } catch (error: any) {
      console.error('Failed to load lead finance summary:', error);
    }
  };

  /**
   * 加载关联线索财务记录列表
   */
  const loadLeadFinanceRecords = async () => {
    if (!caseData?.lead_id) return;
    
    try {
      const data = await getFinanceRecords(EntityType.LEAD, caseData.lead_id);
      const normalizedRecords: FinanceRecord[] = (data.items || []).map(record => ({
        ...record,
        amount: typeof record.amount === 'string' 
          ? parseFloat(record.amount) 
          : record.amount,
        updated_at: record.updated_at || record.created_at,
      }));
      setLeadRecords(normalizedRecords);
    } catch (error: any) {
      console.error('Failed to load lead finance records:', error);
    }
  };

  /**
   * 加载所有财务数据
   */
  const loadFinanceData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadFinanceSummary(),
        loadFinanceRecords(),
        loadFinanceConfig(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 加载关联线索财务数据
   */
  const loadLeadFinanceData = async () => {
    if (!caseData?.lead_id) return;
    
    setLeadLoading(true);
    try {
      await Promise.all([loadLeadFinanceSummary(), loadLeadFinanceRecords()]);
    } finally {
      setLeadLoading(false);
    }
  };

  /**
   * 刷新财务数据
   */
  const refreshFinanceData = () => {
    loadFinanceData();
  };

  /**
   * 打开财务配置弹窗
   */
  const handleOpenConfigModal = () => {
    setConfigModalVisible(true);
  };

  /**
   * 关闭财务配置弹窗
   */
  const handleCloseConfigModal = () => {
    setConfigModalVisible(false);
  };

  /**
   * 财务配置保存成功
   */
  const handleConfigSuccess = () => {
    loadFinanceConfig();
    loadFinanceSummary(); // 刷新财务汇总，因为合同金额可能变化
    handleCloseConfigModal();
  };

  /**
   * 处理删除财务记录
   */
  const handleDeleteRecord = (record: FinanceRecord) => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除这条财务记录吗？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteFinanceRecord(EntityType.CASE, caseId, record.id);
          message.success('财务记录已删除');
          refreshFinanceData();
        } catch (error: any) {
          console.error('Failed to delete finance record:', error);
          message.error('删除失败，请重试');
        }
      },
    });
  };

  /**
   * 处理打开添加财务记录弹窗
   */
  const handleOpenAddModal = () => {
    setModalVisible(true);
  };

  /**
   * 处理关闭添加财务记录弹窗
   */
  const handleCloseAddModal = () => {
    setModalVisible(false);
  };

  /**
   * 处理添加成功
   */
  const handleAddSuccess = () => {
    refreshFinanceData();
    handleCloseAddModal();
  };

  /**
   * 格式化金额显示
   */
  const formatAmount = (amount: number | string, isRefund: boolean = false): React.ReactNode => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const formattedAmount = numAmount.toLocaleString('zh-CN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return (
      <Text style={{ color: isRefund ? '#cf1322' : '#000' }}>
        {isRefund ? '-' : ''}¥{formattedAmount}
      </Text>
    );
  };

  /**
   * 处理查看附件
   */
  const handleViewAttachments = (attachments: any[]) => {
    setSelectedAttachments(attachments);
    setAttachmentModalVisible(true);
  };

  /**
   * 处理预览附件
   */
  const handlePreviewAttachment = async (attachment: any) => {
    try {
      // 动态导入getAttachmentDownloadUrl
      const { getAttachmentDownloadUrl } = await import('../../../../services/finance');
      
      // 获取临时下载URL
      const response = await getAttachmentDownloadUrl(
        EntityType.CASE,
        caseId,
        attachment.id
      );
      
      // 使用 FilePreview 组件预览
      setPreviewFile({
        file_url: response.download_url,
        original_filename: attachment.file_name,
        content_type: attachment.file_type,
      });
      setPreviewVisible(true);
      // 关闭附件列表弹窗
      setAttachmentModalVisible(false);
    } catch (error) {
      console.error('Failed to get attachment URL:', error);
      message.error('获取附件链接失败');
    }
  };

  /**
   * 处理下载附件
   */
  const handleDownloadAttachment = async (attachment: any) => {
    try {
      // 动态导入getAttachmentDownloadUrl
      const { getAttachmentDownloadUrl } = await import('../../../../services/finance');
      
      // 获取临时下载URL
      const response = await getAttachmentDownloadUrl(
        EntityType.CASE,
        caseId,
        attachment.id
      );
      
      // 在新窗口打开下载链接
      window.open(response.download_url, '_blank');
      message.success('开始下载');
    } catch (error) {
      console.error('Failed to download attachment:', error);
      message.error('下载失败');
    }
  };

  // 组件加载时获取财务数据
  useEffect(() => {
    if (caseId) {
      loadFinanceData();
      loadLeadFinanceData();
    }
  }, [caseId, caseData?.lead_id]);

  // 基础列定义（不包含操作列）
  const baseColumns: ColumnsType<FinanceRecord> = [
    {
      title: '费用类型',
      dataIndex: 'expense_type',
      key: 'expense_type',
      width: 60,
      render: (type: string) => {
        const typeMap: Record<string, string> = {
          CONSULTATION_FEE: '咨询费',
          LAWYER_FEE: '律师费',
          CONSULTANT_FEE: '顾问费',
          SERVICE_FEE: '服务费',
          TRAVEL_FEE: '差旅费',
          OTHER: '其他',
        };
        return typeMap[type] || type;
      },
    },
    {
      title: '款项性质',
      dataIndex: 'payment_nature',
      key: 'payment_nature',
      width: 50,
      render: (nature: string) => {
        const natureMap: Record<string, { text: string; color: string }> = {
          RECEIVED: { text: '已收款', color: 'success' },
          RECEIVABLE: { text: '应收款', color: 'warning' },
          REFUND: { text: '退款', color: 'error' },
        };
        const config = natureMap[nature] || { text: nature, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '关联节点',
      dataIndex: 'milestone_id',
      key: 'milestone_id',
      width: 80,
      render: (milestoneId: number | undefined, record: FinanceRecord) => {
        if (!milestoneId || !financeConfig?.milestones) {
          return <Text type="secondary">-</Text>;
        }
        const milestone = financeConfig.milestones.find(m => m.id === milestoneId);
        if (!milestone) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tag color="blue" style={{ cursor: 'pointer' }}>
            {milestone.milestone_name}
          </Tag>
        );
      },
    },
    {
      title: '动款金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 60,
      render: (amount: number, record: FinanceRecord) => {
        const isRefund = record.payment_nature === 'REFUND';
        return formatAmount(amount, isRefund);
      },
    },
    {
      title: '动款时间',
      dataIndex: 'payment_time',
      key: 'payment_time',
      width: 100,
      render: (time: string) => 
        time ? dayjs(time).format('YYYY-MM-DD HH:mm') : <Text type="secondary">-</Text>,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 120,
      ellipsis: true,
      render: (remark: string) => remark || <Text type="secondary">-</Text>,
    },
    {
      title: '附件',
      dataIndex: 'attachments',
      key: 'attachments',
      width: 40,
      align: 'center',
      render: (attachments: any[]) => {
        if (!attachments || attachments.length === 0) {
          return <Text type="secondary">-</Text>;
        }

        return (
          <div 
            style={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewAttachments(attachments);
            }}
          >
            <Badge count={attachments.length} showZero={false}>
              <PaperClipOutlined style={{ fontSize: 16 }} />
            </Badge>
          </div>
        );
      },
    },
  ];

  // 案件财务记录表格列定义（包含操作列）
  const columns: ColumnsType<FinanceRecord> = [
    ...baseColumns,
    {
      title: '操作',
      key: 'action',
      width: 60,
      render: (_, record) => (
        canEdit && (
          <Button
            type="link"
            size="small"
            danger
            onClick={() => handleDeleteRecord(record)}
          >
            删除
          </Button>
        )
      ),
    },
  ];

  // 关联线索财务记录表格列定义（只读，无操作列）
  const leadColumns: ColumnsType<FinanceRecord> = baseColumns;

  // 获取付费模式显示文本
  const getPaymentModeText = (mode: PaymentMode) => {
    const modeMap = {
      [PaymentMode.FIXED]: '固定收费',
      [PaymentMode.MILESTONE]: '节点付费',
      [PaymentMode.RISK_AGENCY]: '风险代理',
      [PaymentMode.HOURLY]: '按小时计费',
    };
    return modeMap[mode] || mode;
  };

  // 显示加载状态
  if (loading && records.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="加载财务数据中..." />
      </div>
    );
  }

  // 渲染关联线索财务记录区域
  const renderLeadFinanceSection = () => {
    if (!caseData?.lead_id) return null;

    return (
      <>
        {/* 关联线索财务汇总 */}
        <Card
          title="关联线索财务汇总"
          style={{ marginTop: 40, marginBottom: 24 }}
          bordered={false}
          loading={leadLoading}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="合同金额"
                value={leadSummary?.contract_amount || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="已收金额"
                value={leadSummary?.total_received || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="待收金额"
                value={leadSummary?.total_receivable || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="净收款"
                value={leadSummary?.net_received || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: leadSummary && (typeof leadSummary.net_received === 'number' ? leadSummary.net_received : parseFloat(String(leadSummary.net_received))) >= 0 ? '#52c41a' : '#ff4d4f' }}
              />
            </Col>
          </Row>
        </Card>

        {/* 关联线索财务记录列表 */}
        <Card
          title="关联线索财务记录"
          bordered={false}
        >
          <Table
            columns={leadColumns}
            dataSource={leadRecords}
            rowKey="id"
            loading={leadLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
            }}
            locale={{
              emptyText: (
                <div style={{ padding: '40px 0', color: '#999' }}>
                  <p>暂无关联线索财务记录</p>
                </div>
              ),
            }}
            scroll={undefined}
          />
        </Card>
      </>
    );
  };

  return (
    <div className="case-finance-tab">
      {/* 财务配置和汇总整合卡片 */}
      <Card
        title="财务概览"
        style={{ marginBottom: 24 }}
        extra={
          canEdit && (
            <Space>
              {financeConfig && financeConfig.id > 0 ? (
                <Button
                  type="link"
                  icon={<SettingOutlined />}
                  onClick={handleOpenConfigModal}
                >
                  编辑配置
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<SettingOutlined />}
                  onClick={handleOpenConfigModal}
                >
                  配置财务模式
                </Button>
              )}
            </Space>
          )
        }
        loading={loadingConfig || loading}
      >
        {financeConfig ? (
          <>
            {/* 财务配置信息 */}
            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* 基本信息 */}
                <Space split={<Divider type="vertical" />} size="large" wrap>
                  <Text>
                    付费模式：<Text strong>{getPaymentModeText(financeConfig.payment_mode)}</Text>
                  </Text>
                  <Text>
                    合同金额：<Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                      ¥{Number(financeConfig.contract_amount).toLocaleString('zh-CN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </Text>
                  {financeConfig.payment_mode === PaymentMode.MILESTONE && financeConfig.milestone_progress && (
                    <Text>
                      节点进度：<Text strong style={{ color: '#52c41a' }}>
                        {financeConfig.milestone_progress.paid_milestones}/{financeConfig.milestone_progress.total_milestones} 已完成
                      </Text>
                    </Text>
                  )}
                </Space>

                {/* 风险代理详细信息 */}
                {financeConfig.payment_mode === PaymentMode.RISK_AGENCY && (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#f6ffed', 
                    border: '1px solid #b7eb8f',
                    borderRadius: 4,
                  }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong style={{ color: '#52c41a' }}>风险代理详情</Text>
                      <Space split={<Divider type="vertical" />} size="large" wrap>
                        {financeConfig.risk_base_fee && (
                          <Text>
                            基础费用：<Text strong>¥{Number(financeConfig.risk_base_fee).toLocaleString('zh-CN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}</Text>
                          </Text>
                        )}
                        {financeConfig.risk_success_rate && (
                          <Text>
                            成功比例：<Text strong>{(Number(financeConfig.risk_success_rate) * 100).toFixed(2)}%</Text>
                          </Text>
                        )}
                        {financeConfig.risk_target_amount && (
                          <Text>
                            预期标的：<Text strong>¥{Number(financeConfig.risk_target_amount).toLocaleString('zh-CN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}</Text>
                          </Text>
                        )}
                      </Space>
                      {(financeConfig.risk_min_fee || financeConfig.risk_max_fee) && (
                        <Space split={<Divider type="vertical" />} size="large">
                          {financeConfig.risk_min_fee && (
                            <Text>
                              最低收费：<Text strong>¥{Number(financeConfig.risk_min_fee).toLocaleString('zh-CN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}</Text>
                            </Text>
                          )}
                          {financeConfig.risk_max_fee && (
                            <Text>
                              最高收费：<Text strong>¥{Number(financeConfig.risk_max_fee).toLocaleString('zh-CN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}</Text>
                            </Text>
                          )}
                        </Space>
                      )}
                      {financeConfig.risk_payment_condition && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          付费条件：{financeConfig.risk_payment_condition}
                        </Text>
                      )}
                    </Space>
                  </div>
                )}

                {/* 按小时计费详细信息 */}
                {financeConfig.payment_mode === PaymentMode.HOURLY && (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#e6f7ff', 
                    border: '1px solid #91d5ff',
                    borderRadius: 4,
                  }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong style={{ color: '#1890ff' }}>按小时计费详情</Text>
                      <Space split={<Divider type="vertical" />} size="large" wrap>
                        {financeConfig.hourly_rate && (
                          <Text>
                            小时费率：<Text strong>¥{Number(financeConfig.hourly_rate).toLocaleString('zh-CN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}/小时</Text>
                          </Text>
                        )}
                        {financeConfig.estimated_hours && (
                          <Text>
                            预估工时：<Text strong>{financeConfig.estimated_hours} 小时</Text>
                          </Text>
                        )}
                        {financeConfig.hourly_rate && financeConfig.estimated_hours && (
                          <Text>
                            预估总费用：<Text strong style={{ color: '#1890ff' }}>
                              ¥{(Number(financeConfig.hourly_rate) * financeConfig.estimated_hours).toLocaleString('zh-CN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Text>
                          </Text>
                        )}
                      </Space>
                    </Space>
                  </div>
                )}

                {/* 备注 */}
                {financeConfig.notes && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    备注：{financeConfig.notes}
                  </Text>
                )}
              </Space>
            </div>

            {/* 财务统计 */}
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="已收金额"
                  value={summary?.total_received || 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="待收金额"
                  value={summary?.total_receivable || 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="退款金额"
                  value={summary?.total_refund || 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="净收款"
                  value={summary?.net_received || 0}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ 
                    color: summary && (typeof summary.net_received === 'number' ? summary.net_received : parseFloat(String(summary.net_received))) >= 0 ? '#52c41a' : '#ff4d4f' 
                  }}
                />
              </Col>
            </Row>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
              尚未配置财务模式，建议先配置后再添加财务记录
            </Text>
            {canEdit && (
              <Button
                type="primary"
                icon={<SettingOutlined />}
                onClick={handleOpenConfigModal}
              >
                立即配置
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* 节点付费进度展示 */}
      {financeConfig?.payment_mode === PaymentMode.MILESTONE && financeConfig.milestone_progress && (
        <MilestoneProgress
          caseId={caseId}
          milestones={financeConfig.milestones || []}
          progress={financeConfig.milestone_progress}
          contractAmount={financeConfig.contract_amount}
          canEdit={canEdit}
          onRefresh={loadFinanceConfig}
        />
      )}

      {/* 如果是节点付费模式但还没有节点，显示空的进度卡片 */}
      {financeConfig?.payment_mode === PaymentMode.MILESTONE && 
       (!financeConfig.milestones || financeConfig.milestones.length === 0) && (
        <MilestoneProgress
          caseId={caseId}
          milestones={[]}
          progress={{
            total_milestones: 0,
            pending_milestones: 0,
            paid_milestones: 0,
            overdue_milestones: 0,
            total_amount: 0,
            paid_amount: 0,
            remaining_amount: 0,
            payment_progress: 0,
          }}
          contractAmount={financeConfig.contract_amount}
          canEdit={canEdit}
          onRefresh={loadFinanceConfig}
        />
      )}

      {/* 财务记录列表 */}
      <Card
        title="财务记录"
        extra={
          canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenAddModal}
            >
              添加财务记录
            </Button>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', color: '#999' }}>
                <p>暂无财务记录</p>
                {canEdit && (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={handleOpenAddModal}
                  >
                    添加第一条财务记录
                  </Button>
                )}
              </div>
            ),
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 关联线索财务记录区域 */}
      {renderLeadFinanceSection()}

      {/* 添加财务记录弹窗 */}
      <AddCaseFinanceRecordModal
        visible={modalVisible}
        caseId={caseId}
        onCancel={handleCloseAddModal}
        onSuccess={handleAddSuccess}
      />

      {/* 财务配置弹窗 */}
      <FinanceConfigModal
        visible={configModalVisible}
        caseId={caseId}
        config={financeConfig}
        onCancel={handleCloseConfigModal}
        onSuccess={handleConfigSuccess}
      />

      {/* 附件列表弹窗 */}
      <Modal
        title="附件列表"
        open={attachmentModalVisible}
        onCancel={() => setAttachmentModalVisible(false)}
        footer={null}
        width={600}
      >
        <List
          dataSource={selectedAttachments}
          renderItem={(attachment: any) => (
            <List.Item
              actions={[
                <Button
                  key="preview"
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => handlePreviewAttachment(attachment)}
                >
                  预览
                </Button>,
                <Button
                  key="download"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadAttachment(attachment)}
                >
                  下载
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={<PaperClipOutlined style={{ fontSize: 20 }} />}
                title={attachment.file_name}
                description={`大小: ${attachment.file_size ? (attachment.file_size / 1024).toFixed(2) + ' KB' : '未知'}`}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* 文件预览弹窗 */}
      {previewFile && (
        <FilePreview
          visible={previewVisible}
          fileUrl={previewFile.file_url}
          fileName={previewFile.original_filename}
          fileType={previewFile.content_type}
          onClose={() => {
            setPreviewVisible(false);
            setPreviewFile(null);
            // 重新打开附件列表
            setAttachmentModalVisible(true);
          }}
          onDownload={() => {
            const attachment = selectedAttachments.find(
              att => att.file_name === previewFile.original_filename
            );
            if (attachment) {
              handleDownloadAttachment(attachment);
            }
          }}
        />
      )}
    </div>
  );
};

export default CaseFinanceTab;
